import { useState } from 'react';
import {
  Box, Button, ButtonBase, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import { EXPORTS } from '../../api/ads';
import { formatDay } from '../../utils/dates';
import { isoDate } from '../../utils/format';
import DownloadMenu from '../table/DownloadMenu';
import StickyTableContainer from '../table/StickyTableContainer';
import { METRICS, bucketDaily, formatAxis, formatMetric, metricValue } from './metrics';

const CHART_METRICS = METRICS.filter((metric) => metric.chart);
const ALL = 'all';
const LINE = 'line';
const BAR = 'bar';
const tickFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const longRangeTickFormat = new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' });
const yearFormat = new Intl.DateTimeFormat(undefined, { year: 'numeric' });
const UNITS = [{ value: 'day', label: 'Day' }, { value: 'month', label: 'Month' }, { value: 'year', label: 'Year' }];
const UNIT_LABELS = {
  day: (value) => formatDay(isoDate(value)),
  month: (value) => longRangeTickFormat.format(value),
  year: (value) => yearFormat.format(value),
};
const TOGGLE_SX = { px: 1.5, textTransform: 'none' };

/**
 * Trend of one metric (a single series: the title names it, so no legend), or All: every metric on one chart, each
 * scaled to its own peak because their units differ (the tooltip shows the real values). As lines or bars, with a
 * crosshair tooltip, or the same periods as a table, by day, month or year. metricId, chartType, unit and their
 * on...Change keep the choices in the page's URL.
 */
export default function TrendChart({
  daily, currency, metricId, onMetricChange, chartType = LINE, onChartTypeChange, unit = 'day', onUnitChange, params,
}) {
  const [asTable, setAsTable] = useState(false);
  const [hidden, setHidden] = useState([]);
  const all = metricId === ALL;
  const metric = CHART_METRICS.find((item) => item.id === metricId) ?? CHART_METRICS[0];
  const metrics = all ? CHART_METRICS : [metric];
  const bars = chartType === BAR;
  // the metrics switched off in All; the last one showing stays on
  const toggleHidden = (id) => setHidden((current) => {
    if (current.includes(id)) return current.filter((item) => item !== id);
    return current.length + 1 < CHART_METRICS.length ? [...current, id] : current;
  });

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="flex-start"
             sx={{ mb: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' }, rowGap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
            {all ? 'All metrics' : metric.label} by {unit}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {all ? 'Each metric is scaled to its own highest value (100%). Hover for the real values.'
              : `Hover the chart for each ${unit}'s value.`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Button size="small" onClick={() => setAsTable((value) => !value)} sx={{ whiteSpace: 'nowrap' }}
                  startIcon={asTable ? <ShowChartIcon /> : <TableRowsOutlinedIcon />}>
            {asTable ? 'Show chart' : 'Show table'}
          </Button>
          <DownloadMenu path={EXPORTS.daily} params={params} label="Daily totals" />
        </Stack>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center"
             sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={all ? ALL : metric.id} aria-label="Metric"
                           onChange={(_, value) => value && onMetricChange(value)}>
          <ToggleButton value={ALL} sx={TOGGLE_SX}>All</ToggleButton>
          {CHART_METRICS.map((item) => (
            <ToggleButton key={item.id} value={item.id} sx={TOGGLE_SX}>{item.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <ToggleButtonGroup size="small" exclusive value={unit} aria-label="Group by"
                             onChange={(_, value) => value && onUnitChange(value)}>
            {UNITS.map((item) => (
              <ToggleButton key={item.value} value={item.value} sx={TOGGLE_SX}>{item.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          {!asTable && (
            <ToggleButtonGroup size="small" exclusive value={bars ? BAR : LINE} aria-label="Chart type"
                               onChange={(_, value) => value && onChartTypeChange(value)}>
              <ToggleButton value={LINE} aria-label="Line graph" sx={TOGGLE_SX}>
                <ShowChartIcon fontSize="small" sx={{ mr: 0.5 }} />Line
              </ToggleButton>
              <ToggleButton value={BAR} aria-label="Bar graph" sx={TOGGLE_SX}>
                <BarChartIcon fontSize="small" sx={{ mr: 0.5 }} />Bar
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
      </Stack>

      {asTable ? <PeriodTable daily={daily} currency={currency} unit={unit} /> : (
        <>
          {all && <MetricLegend hidden={hidden} onToggle={toggleHidden} />}
          <Chart daily={daily} currency={currency} indexed={all} bars={bars} unit={unit}
                 metrics={metrics.filter((item) => !all || !hidden.includes(item.id))} />
        </>
      )}
    </Paper>
  );
}

// All's legend: each colour square and name turns its metric on and off
function MetricLegend({ hidden, onToggle }) {
  return (
    <Stack direction="row" role="group" aria-label="Metrics shown"
           sx={{ flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, mb: 1 }}>
      {CHART_METRICS.map((metric) => {
        const on = !hidden.includes(metric.id);
        return (
          <ButtonBase key={metric.id} aria-pressed={on} onClick={() => onToggle(metric.id)}
                      title={`${on ? 'Hide' : 'Show'} ${metric.label}`}
                      sx={{ gap: 0.75, py: 0.5, borderRadius: 1, '&.Mui-focusVisible': { outline: 2,
                        outlineColor: 'primary.main', outlineOffset: 2 } }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', border: 2, borderColor: metric.color,
              bgcolor: on ? metric.color : 'transparent', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: on ? 'text.primary' : 'text.disabled',
              textDecoration: on ? 'none' : 'line-through' }}>
              {metric.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Stack>
  );
}

function Chart({ daily, currency, metrics, indexed, bars, unit }) {
  const theme = useTheme();
  const entries = bucketDaily(daily, unit);
  const dates = entries.map((entry) => toDate(entry.date));
  const ticks = unit === 'year' ? yearFormat
    : unit === 'month' || daily.length > 366 ? longRangeTickFormat : tickFormat;

  const series = metrics.map((metric) => {
    const values = entries.map((entry) => metricValue(entry, metric));
    const peak = Math.max(...values.map((value) => value ?? 0));
    const scaled = (value) => (value == null || !peak ? value : (value * 100) / peak);
    return {
      id: metric.id,
      data: indexed ? values.map(scaled) : values,
      label: metric.label,
      color: metric.color,
      valueFormatter: (_, { dataIndex }) => formatMetric(values[dataIndex], metric, currency),
      // a month or year is few points, and a single one has no line to show it
      ...(bars ? {} : { area: !indexed, showMark: unit !== 'day', curve: 'linear' }),
    };
  });
  const shared = {
    height: 300,
    margin: { top: 16, right: 16, bottom: 32, left: 64 },
    xAxis: [{
      data: dates,
      scaleType: bars ? 'band' : unit === 'day' ? 'time' : 'point',
      valueFormatter: (value, context) => (context.location === 'tick' ? ticks.format(value)
        : UNIT_LABELS[unit](value)),
    }],
    yAxis: [indexed ? { min: 0, max: 100, valueFormatter: (value) => `${value}%` }
      : { valueFormatter: (value) => formatAxis(value, metrics[0], currency) }],
    series,
    grid: { horizontal: true },
    slotProps: { legend: { hidden: true } },
    sx: {
      '& .MuiLineElement-root': { strokeWidth: 2 },
      '& .MuiAreaElement-root': { fillOpacity: 0.12 },
      '& .MuiChartsGrid-line': { stroke: theme.palette.divider },
      '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: theme.palette.divider },
      '& .MuiChartsAxis-tickLabel': { fill: theme.palette.text.secondary },
    },
  };

  return bars ? <BarChart {...shared} borderRadius={4} tooltip={{ trigger: 'axis' }} /> : <LineChart {...shared} />;
}

function PeriodTable({ daily, currency, unit }) {
  return (
    <StickyTableContainer sx={{ maxHeight: 340, border: 1, borderColor: 'divider', borderRadius: 2 }}>
      <Table size="small" stickyHeader aria-label={`Insights by ${unit}`}>
        <TableHead>
          <TableRow>
            <TableCell>{UNITS.find((item) => item.value === unit).label}</TableCell>
            {METRICS.map((metric) => <TableCell key={metric.id} align="right">{metric.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {bucketDaily(daily, unit).map((entry) => (
            <TableRow key={entry.date} hover>
              <TableCell>{UNIT_LABELS[unit](toDate(entry.date))}</TableCell>
              {METRICS.map((metric) => (
                <TableCell key={metric.id} align="right">
                  {formatMetric(metricValue(entry, metric), metric, currency)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </StickyTableContainer>
  );
}

// YYYY-MM-DD as a local date, so the axis does not shift days across time zones
function toDate(value) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}
